// frontend/src/components/Texpact/OrderList.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { texpactAPI } from '../../api/client';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './OrderList.css';

const OrderList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const savedState = location.state?.returnState;

  const [partners,        setPartners]        = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  const [orders,  setOrders]  = useState([]);
  const [supplierRestriction, setSupplierRestriction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Filters
  const [partnerId,    setPartnerId]    = useState(savedState?.partnerId    || location.state?.partnerId || 'ALL');
  const [statusFilter, setStatusFilter] = useState(savedState?.statusFilter || 'NEW,PARTIAL');
  const [searchTerm,   setSearchTerm]   = useState(savedState?.searchTerm   || '');
  const [fromDate,     setFromDate]     = useState(savedState?.fromDate     || '');
  const [toDate,       setToDate]       = useState(savedState?.toDate       || '');

  // Pagination
  const [currentPage,  setCurrentPage]  = useState(savedState?.currentPage  || 1);
  const [itemsPerPage, setItemsPerPage] = useState(savedState?.itemsPerPage || 25);
  const [totalPages,   setTotalPages]   = useState(0);
  const [totalItems,   setTotalItems]   = useState(0);

  const searchDebounce = useRef(null);

  useEffect(() => { loadPartners(); }, []);
  useEffect(() => { loadOrders(); }, [partnerId, statusFilter, searchTerm, fromDate, toDate, currentPage, itemsPerPage]);

  const loadPartners = async () => {
    try {
      setLoadingPartners(true);
      const res = await texpactAPI.getPartners();
      if (res.data.success) setPartners(res.data.partners);
    } catch (err) {
      console.error('Error loading partners:', err);
    } finally {
      setLoadingPartners(false);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (fromDate)  params.append('fromDate',  fromDate);
      if (toDate)    params.append('toDate',    toDate);
      params.append('page',  currentPage);
      params.append('limit', itemsPerPage);

      const res = await texpactAPI.getByPartner(partnerId, params.toString());
      if (res.data.success) {
        setOrders(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.totalItems);
        if (res.data.supplierRestriction) setSupplierRestriction(res.data.supplierRestriction);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const getOrderUrl = (publicId) => `/orders/view/${publicId}`;

  const handleSelectOrder = (publicId) => {
    console.log("publicId: ", publicId);
    
    navigate(getOrderUrl(publicId), {
      state: {
        returnTo: '/orders',
        partnerId,
        returnState: { partnerId, statusFilter, searchTerm, fromDate, toDate, currentPage, itemsPerPage },
      },
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      NEW:      { className: 'status-new',      icon: '⭕', label: 'Novo'      },
      PARTIAL:   { className: 'status-partial',   icon: '⏳', label: 'Parcial'   },
      CLOSED:   { className: 'status-closed', icon: '✓',  label: 'Fechado' },
      CANCELLED: { className: 'status-cancelled', icon: '✗',  label: 'Cancelado' },
    };
    const info = map[status] || { className: 'status-new', icon: '?', label: status };
    return <span className={`status-badge ${info.className}`}>{info.icon} {info.label}</span>;
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      header: 'Ord Prod',
      field: 'po_code',
      className: 'code-column',
      render: (row) => (
        <a
          href={getOrderUrl(row.public_id)}
          className="order-link order-link--code"
          onClick={e => { e.preventDefault(); handleSelectOrder(row.public_id); }}
          onAuxClick={e => e.stopPropagation()}
        >
          {row.po_code}
        </a>
      ),
    },
    { header: 'Fornecedor',      field: 'supplier_name' },
    { header: 'Cod Fornecedor', field: 'supplier_code' },
    {
      header: 'Data Entrega',
      field: 'delivery_date',
      render: (row) => new Date(row.delivery_date).toLocaleDateString(),
    },
    { header: 'Linha',     field: 'line_count',    className: 'text-center' },
    { header: 'Qtd Total', field: 'total_qty',     className: 'text-right',
      render: (row) => parseFloat(row.total_qty    || 0).toFixed(0) },
    { header: 'Enviado',   field: 'total_shipped', className: 'text-right',
      render: (row) => parseFloat(row.total_shipped || 0).toFixed(0) },
    { header: 'Pendente',   field: 'total_pending', className: 'text-right',
      render: (row) => parseFloat(row.total_pending || 0).toFixed(0) },
    {
      header: 'Estado',
      field: 'status',
      render: (row) => (
        <a
          href={getOrderUrl(row.public_id)}
          className="order-link"
          onClick={e => { e.preventDefault(); handleSelectOrder(row.public_id); }}
          onAuxClick={e => e.stopPropagation()}
        >
          {getStatusBadge(row.status)}
        </a>
      ),
    },
  ];

  if (loadingPartners) {
    return <div className="order-list"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="order-list">

      <div className="order-filters">
        <div className="filters-row">

          {!supplierRestriction && (
            <div className="filter-group">
              <label>Fornecedor</label>
              <select value={partnerId} onChange={e => { setPartnerId(e.target.value); setCurrentPage(1); }}>
                <option value="ALL">Todos os Fornecedores</option>
                {partners.map(p => (
                  <option key={p.value} value={p.value}>{p.label} ({p.order_count})</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Pesquisa</label>
            <input
              type="text"
              placeholder="Ord Prod, Fornecedor..."
              value={searchTerm}
              onChange={e => {
                const val = e.target.value;
                setSearchTerm(val);
                clearTimeout(searchDebounce.current);
                searchDebounce.current = setTimeout(() => setCurrentPage(1), 350);
              }}
            />
          </div>

          <div className="filter-group">
            <label>Estado</label>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="ALL">Todos</option>
              <option value="NEW">Novo</option>
              <option value="PARTIAL">Parcial</option>
              <option value="NEW,PARTIAL">Novo & Parcial</option>
              <option value="CLOSED">Fechado</option>
              <option value="CANCELLED">Cancelado</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Data Desde</label>
            <input type="date" value={fromDate}
              onChange={e => { setFromDate(e.target.value); setCurrentPage(1); }} />
          </div>

          <div className="filter-group">
            <label>Data Até</label>
            <input type="date" value={toDate}
              onChange={e => { setToDate(e.target.value); setCurrentPage(1); }} />
          </div>

        </div>
      </div>

      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        error={error}
        onRetry={loadOrders}
        onRowClick={(row) => handleSelectOrder(row.public_id)}
        emptyMessage="No orders found"
      />

      {!loading && !error && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={v => { setItemsPerPage(v); setCurrentPage(1); }}
        />
      )}

    </div>
  );
};

export default OrderList;